/**
 * swym-wishlist-page.js (Lazy + Shopify Markets prices + compare-at + %OFF badge + hover crossfade 2nd image)
 * - Loads wishlist products from Swym
 * - Renders in chunks (infinite scroll)
 * - Fetches *correct* price for current Market via /variants/{id}.js
 * - Shows compare-at price and %OFF badge when on sale
 * - Hover on card crossfades to next product image (desktop hover devices only)
 * - Removes persistently using Swym deleteEvent (so items don't come back after refresh)
 *
 * + Updates:
 *   - Chunk size = 16
 *   - Shows a small skeleton "chunk loading" while next chunk is being appended/hydrated
 *   - Adds extra guards for huge wishlists (prevents double-load loops)
 */

(() => {
  const root = document.getElementById("sora-wishlist");
  if (!root) return;

  const els = {
    loading: root.querySelector(".sora-wl-loading"),
    empty: root.querySelector(".sora-wl-empty"),
    grid: root.querySelector(".sora-wl-grid"),
    count: document.querySelector(".sora-wl-count-wishlisted"),
  };

  if (!els.grid) return;

  // ----- Tunables
  const PAGE_SIZE = 16;   // ✅ items per chunk
  const CONCURRENCY = 8;  // simultaneous variant fetches
  const ROOT_MARGIN = "800px"; // slightly larger so you never see “blank” while scrolling

  let allProducts = [];
  let cursor = 0;
  let sentinel = null;
  let io = null;

  // Guards
  let isRendering = false;
  let done = false;

  const variantCache = new Map(); // variantId:number -> Promise<variant|null>
  const productCache = new Map(); // handle:string -> Promise<product|null>

  const show = (el) => el && (el.hidden = false);
  const hide = (el) => el && (el.hidden = true);

  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  // ---- Swym field helpers (forgiving)
  const getEventId = (p) => p?._id || p?.eid || p?.eventId || p?.event_id || "";
  const getVariantIdRaw = (p) => p?.epi || p?.variantId || p?.variant_id || "";
  const getProductId = (p) => p?.empi || p?.productId || p?.product_id || "";
  const getUrl = (p) => p?.du || p?.url || p?.product_url || "#";
  const getImg = (p) => p?.iu || p?.image || p?.img || "";
  const getTitle = (p) => p?.dt || p?.title || "";

  function normalizeVariantId(input) {
    if (input == null) return null;
    if (typeof input === "number" && Number.isFinite(input)) return input;

    const s = String(input).trim();
    if (!s) return null;

    const gidMatch = s.match(/ProductVariant\/(\d+)/);
    if (gidMatch) return Number(gidMatch[1]);

    if (/^\d+$/.test(s)) return Number(s);

    const digits = s.match(/(\d{6,})/);
    if (digits) return Number(digits[1]);

    return null;
  }

  function setCount(n) {
    if (!els.count) return;
    els.count.textContent = n ? `${n} item${n === 1 ? "" : "s"}` : "";
  }

  function getCardData(card) {
    try {
      return JSON.parse(decodeURIComponent(card.dataset.payload));
    } catch {
      return null;
    }
  }

  // Symbol-only money (e.g. "$22.38", "€21.95") — no ISO currency codes.
  function formatMoneyCents(cents) {
    const currency =
      window.SORA_WL?.currency ||
      window.Shopify?.currency?.active ||
      "CAD";
    const locale =
      (window.SORA_WL?.locale || navigator.language || "en-CA").replace("_", "-");

    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency, currencyDisplay: "narrowSymbol" }).format((cents || 0) / 100);
    } catch {
      return ((cents || 0) / 100).toFixed(2);
    }
  }

  async function fetchVariant(variantId) {
    const id = normalizeVariantId(variantId);
    if (!id) return null;

    if (variantCache.has(id)) return variantCache.get(id);

    const p = fetch(`/variants/${id}.js`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

    variantCache.set(id, p);
    return p;
  }

  function getHandleFromUrl(url) {
    try {
      const u = new URL(url, window.location.origin);
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.indexOf("products");
      if (idx !== -1 && parts[idx + 1]) return parts[idx + 1];
    } catch {}
    return null;
  }

  async function fetchProductByHandle(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);

    const p = fetch(`/products/${handle}.js`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

    productCache.set(handle, p);
    return p;
  }

  function preloadImage(src) {
    if (!src) return;
    const img = new Image();
    img.decoding = "async";
    img.src = src;
  }

  function stripQuery(u) {
    try {
      return String(u || "").split("?")[0];
    } catch {
      return String(u || "");
    }
  }

  function stripShopifySizeSuffix(u) {
    const s = stripQuery(u);
    return s.replace(/_(\d+)(x\d+)?(?=\.[a-zA-Z]{3,4}$)/, "");
  }

  function normalizeImgForCompare(u) {
    return stripShopifySizeSuffix(u);
  }

  function chooseNextImage(images, currentUrl) {
    const imgs = (images || []).filter(Boolean);
    if (imgs.length < 2) return null;

    const cur = normalizeImgForCompare(currentUrl);
    const idx = imgs.findIndex((u) => normalizeImgForCompare(u) === cur);

    if (idx >= 0) return imgs[(idx + 1) % imgs.length];
    return imgs[1] || null;
  }

  async function runPool(tasks, limit) {
    const executing = new Set();
    const results = [];

    for (const task of tasks) {
      const p = Promise.resolve().then(task);
      results.push(p);
      executing.add(p);

      const cleanup = () => executing.delete(p);
      p.then(cleanup).catch(cleanup);

      if (executing.size >= limit) {
        await Promise.race(executing);
      }
    }

    return Promise.allSettled(results);
  }

  // ---- Chunk loading UI
  function addChunkLoader(count) {
    removeChunkLoader();

    const wrap = document.createElement("div");
    wrap.className = "sora-wl-chunkLoading";
    wrap.setAttribute("data-wl-chunk-loading", "1");

    for (let i = 0; i < count; i++) {
      const sk = document.createElement("div");
      sk.className = "sora-wl-chunkSkel";
      wrap.appendChild(sk);
    }

    els.grid.appendChild(wrap);
  }

  function removeChunkLoader() {
    els.grid.querySelectorAll('[data-wl-chunk-loading="1"]').forEach((n) => n.remove());
  }

  async function hydrateCards(cards) {
    const hoverCapable =
      !!window.matchMedia &&
      window.matchMedia("(hover:hover) and (pointer:fine)").matches;

    const tasks = cards.map((card) => async () => {
      const data = getCardData(card);
      const priceEl = card.querySelector("[data-wl-price]");
      const compareEl = card.querySelector("[data-wl-compare]");
      const saleEl = card.querySelector("[data-wl-sale]");

      if (!data || !priceEl) return;

      // Reset placeholders
      priceEl.textContent = "";
      if (compareEl) {
        compareEl.textContent = "";
        compareEl.hidden = true;
      }
      if (saleEl) {
        saleEl.textContent = "";
        saleEl.hidden = true;
      }

      // Prices
      const v = await fetchVariant(data.epi);
      if (!v || typeof v.price !== "number") return;

      const price = v.price;
      const compare = typeof v.compare_at_price === "number" ? v.compare_at_price : null;

      priceEl.innerHTML = formatMoneyCents(price);

      if (compare && compare > price) {
        if (compareEl) {
          compareEl.innerHTML = formatMoneyCents(compare);
          compareEl.hidden = false;
        }

        card.classList.add("is-on-sale");

        if (saleEl) {
          const pct = Math.round(((compare - price) / compare) * 100);
          if (Number.isFinite(pct) && pct >= 1 && pct <= 95) {
            saleEl.textContent = `${pct}% OFF`;
            saleEl.hidden = false;
          }
        }
      } else {
        card.classList.remove("is-on-sale");
      }

      // Hover crossfade
      if (!hoverCapable) return;
      if (card.dataset.imgSwapInit === "1") return;
      card.dataset.imgSwapInit = "1";

      const primaryImg = card.querySelector(".sora-wl-img--primary");
      const hoverImg = card.querySelector("[data-hover-img]");
      if (!primaryImg || !hoverImg) return;

      const img1 = primaryImg.currentSrc || primaryImg.src;
      if (!img1) return;

      const handle = getHandleFromUrl(data.du);
      const prod = await fetchProductByHandle(handle);
      if (!prod || !Array.isArray(prod.images) || prod.images.length < 2) return;

      const img2 = chooseNextImage(prod.images, img1);
      if (!img2) return;

      preloadImage(img2);
      hoverImg.src = img2;
      card.classList.add("has-hover-img");
    });

    await runPool(tasks, CONCURRENCY);
  }

  async function renderNextChunk() {
    if (done || isRendering) return;
    if (cursor >= allProducts.length) {
      done = true;
      if (io && sentinel) io.unobserve(sentinel);
      return;
    }

    isRendering = true;

    const remaining = allProducts.length - cursor;
    const take = Math.min(PAGE_SIZE, remaining);

    // show loading placeholders for *this* chunk
    addChunkLoader(take);

    try {
      const slice = allProducts.slice(cursor, cursor + take);
      cursor += take;

      const frag = document.createDocumentFragment();
      const newCards = [];

      for (const p of slice) {
        const url = getUrl(p) || "#";
        const title = getTitle(p) || "";
        const img = getImg(p) || "";

        const payload = encodeURIComponent(
          JSON.stringify({
            _id: getEventId(p),
            epi: getVariantIdRaw(p),
            empi: getProductId(p),
            du: url,
          })
        );

        const card = document.createElement("article");
        card.className = "sora-wl-card";
        card.dataset.payload = payload;

        card.innerHTML = `
          <div class="sora-wl-media">
            <a class="sora-wl-mediaLink" href="${escapeHtml(url)}" aria-label="${escapeHtml(title)}">
              ${
                img
                  ? `
                    <img class="sora-wl-img sora-wl-img--primary" src="${escapeHtml(img)}" alt="${escapeHtml(title)}" loading="lazy">
                    <img class="sora-wl-img sora-wl-img--secondary" data-hover-img alt="" loading="lazy" decoding="async">
                  `
                  : ""
              }
            </a>

            <div class="sora-wl-saleBadge" data-wl-sale hidden></div>

            <button class="sora-wl-ctaBar" type="button" data-action="move-to-cart" aria-label="Add to cart">
              ADD TO CART <span aria-hidden="true">+</span>
            </button>
          </div>

          <div class="sora-wl-body">
            <div class="sora-wl-metaRow">
              <div class="sora-wl-rating" data-wl-rating hidden>
                <span class="sora-wl-stars" data-wl-stars></span>
                <span class="sora-wl-ratingCount" data-wl-ratingcount></span>
              </div>

              <button class="sora-wl-heartBtn" type="button" data-action="remove" aria-label="Remove from wishlist">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </button>
            </div>

            <a class="sora-wl-name" href="${escapeHtml(url)}">${escapeHtml(title)}</a>

            <div class="sora-wl-priceRow" aria-live="polite">
              <span class="sora-wl-price" data-wl-price></span>
              <span class="sora-wl-compare" data-wl-compare hidden></span>
            </div>
          </div>
        `;

        frag.appendChild(card);
        newCards.push(card);
      }

      // replace chunk loader with real cards (so you see "load -> pop in")
      removeChunkLoader();
      els.grid.appendChild(frag);

      await hydrateCards(newCards);
    } finally {
      removeChunkLoader();
      isRendering = false;
    }
  }

  async function removeItem(card) {
    const sw = window._swat;
    const data = getCardData(card);
    if (!sw || !data) throw new Error("Missing Swym/card data");

    if (typeof sw.deleteEvent === "function" && data._id) {
      return new Promise((resolve, reject) => {
        try {
          sw.deleteEvent(() => resolve(true), data._id);
        } catch (e) {
          reject(e);
        }
      });
    }

    if (typeof sw.removeFromWishList === "function" && data.epi) {
      return new Promise((resolve, reject) => {
        try {
          try {
            sw.removeFromWishList(
              { epi: data.epi, empi: data.empi, du: data.du },
              () => resolve(true),
              (e) => reject(e)
            );
          } catch {
            sw.removeFromWishList(data.epi, () => resolve(true), (e) => reject(e));
          }
        } catch (e) {
          reject(e);
        }
      });
    }

    if (sw?.platform?.removeFromWishList && data.epi) {
      sw.platform.removeFromWishList(data.epi);
      return true;
    }

    throw new Error("No supported Swym remove method found");
  }

  function moveToCart(card, btn) {
    const sw = window._swat;
    const data = getCardData(card);
    const variantId = normalizeVariantId(data?.epi);
    if (!variantId) return;

    btn.disabled = true;
    const oldLabel = btn.textContent;
    btn.textContent = "Adding…";

    if (!sw?.replayAddToCart) {
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then(() => (window.location.href = "/cart"))
        .catch(() => {
          btn.disabled = false;
          btn.textContent = oldLabel || "ADD TO CART +";
        });
    }

    sw.replayAddToCart(
      { empi: data?.empi, du: data?.du },
      variantId,
      function () {
        btn.textContent = "Added";
        window.location.href = "/cart";
      },
      function () {
        btn.disabled = false;
        btn.textContent = oldLabel || "ADD TO CART +";
      }
    );
  }

  function attachEvents() {
    if (root.dataset.wlBound === "1") return;
    root.dataset.wlBound = "1";

    root.addEventListener("click", async (e) => {
      const btn = e.target.closest("button[data-action]");
      if (!btn) return;

      const card = btn.closest(".sora-wl-card");
      if (!card) return;

      const action = btn.dataset.action;

      if (action === "move-to-cart") {
        moveToCart(card, btn);
        return;
      }

      if (action === "remove") {
        const data = getCardData(card);
        const rawEpi = data?.epi;

        btn.disabled = true;
        card.style.opacity = "0.55";

        try {
          await removeItem(card);
          card.remove();

          if (rawEpi != null) {
            allProducts = allProducts.filter((p) => getVariantIdRaw(p) !== rawEpi);
          }

          setCount(allProducts.length);

          const left = els.grid.querySelectorAll(".sora-wl-card").length;
          if (left === 0) {
            if (cursor < allProducts.length) renderNextChunk();
            else {
              hide(els.grid);
              show(els.empty);
            }
          }
        } catch (err) {
          card.style.opacity = "";
          btn.disabled = false;
          console.warn("[Wishlist] remove failed:", err);
        }
      }
    });
  }

  function setupInfiniteScroll() {
    if (sentinel) sentinel.remove();

    sentinel = document.createElement("div");
    sentinel.setAttribute("data-wl-sentinel", "true");
    sentinel.style.height = "1px";
    sentinel.style.width = "100%";
    sentinel.style.pointerEvents = "none";

    root.appendChild(sentinel);

    if (io) io.disconnect();
    io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) renderNextChunk();
      },
      { rootMargin: ROOT_MARGIN }
    );

    io.observe(sentinel);
  }

  async function init() {
    const sw = window._swat;
    if (!sw?.fetchWrtEventTypeET || !sw.EventTypes) return setTimeout(init, 100);

    // reset state
    done = false;
    isRendering = false;
    cursor = 0;
    variantCache.clear();
    productCache.clear();

    show(els.loading);
    hide(els.empty);
    hide(els.grid);

    const products = await new Promise((resolve) => {
      sw.fetchWrtEventTypeET(resolve, sw.EventTypes.addToWishList);
    });

    allProducts = (products || []).map((p) => {
      try {
        return window.SwymUtils?.formatProductPrice ? window.SwymUtils.formatProductPrice(p) : p;
      } catch {
        return p;
      }
    });

    hide(els.loading);

    if (!allProducts.length) {
      show(els.empty);
      hide(els.grid);
      setCount(0);
      return;
    }

    setCount(allProducts.length);
    show(els.grid);
    hide(els.empty);

    els.grid.innerHTML = "";

    attachEvents();
    setupInfiniteScroll();

    // Render first chunk immediately
    await renderNextChunk();
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(init);
})();
